import {Component, ElementRef, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {finalize} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import Swiper from 'swiper';
import {Autoplay} from 'swiper/modules';
import {BlogApiService} from '../api/blog-api.service';
import {ProfileImageService} from '../api/profile-image.service';
import {Blog} from '../models/blog.model';
import {can} from '../shared/access';

/**
 * Latest blogs, newest first. Used as the /blogs page (stacked cards, "Load more") and, in `compact` mode,
 * as an auto-advancing carousel on the profile overview. `authorId` narrows it to one member's posts.
 */
@Component({
    selector: 'app-blog-feed',
    templateUrl: './blog-feed.component.html',
    styleUrls: ['./blog-feed.component.css']
})
export class BlogFeedComponent implements OnInit, OnDestroy {
    @Input() compact = false;
    @Input() authorId?: string;
    @Input() pageSize = 10;

    blogs: Blog[] = [];
    nextCursor: string | null = null;
    loading = false;
    loaded = false;
    canWrite = false;
    // author photos by member id, filled in as the cards load; a card without an entry shows initials
    avatars: Record<string, string> = {};

    private swiper: Swiper | null = null;

    constructor(private blogApi: BlogApiService, private profileImages: ProfileImageService, private toastr: ToastrService) {
    }

    // The carousel element only exists in compact mode; Swiper is created once it appears.
    @ViewChild('carousel') set carousel(el: ElementRef<HTMLElement> | undefined) {
        if (el && !this.swiper) {
            this.swiper = new Swiper(el.nativeElement, {
                modules: [Autoplay],
                slidesPerView: 1,
                spaceBetween: 0,
                speed: 500,
                rewind: true,
                autoplay: {delay: 4000, disableOnInteraction: false, pauseOnMouseEnter: true},
                breakpoints: {768: {slidesPerView: 2}, 1200: {slidesPerView: 3}},
                on: {
                    // reaching the last card pulls the next page so the carousel keeps going
                    reachEnd: () => {
                        if (this.nextCursor) this.loadMore();
                    }
                }
            });
        } else if (!el && this.swiper) {
            this.swiper.destroy();
            this.swiper = null;
        }
    }

    ngOnInit(): void {
        const perms = JSON.parse(sessionStorage.getItem('rolesPermission') || '{}');
        this.canWrite = can(perms, 'BLOG', 'CREATE');
        this.loadMore();
    }

    ngOnDestroy(): void {
        this.swiper?.destroy();
    }

    loadMore(): void {
        if (this.loading) return;
        this.loading = true;
        this.blogApi.getFeed(this.nextCursor, this.pageSize, this.authorId)
            .pipe(finalize(() => {
                this.loading = false;
                this.loaded = true;
            }))
            .subscribe({
                next: page => {
                    this.blogs = [...this.blogs, ...page.items];
                    page.items.forEach(b => this.loadAvatar(b));
                    this.nextCursor = page.nextCursor;
                    // slides are rendered on the next change-detection pass, then Swiper re-measures
                    setTimeout(() => this.swiper?.update(), 0);
                },
                error: err => {
                    console.error('Failed to load blogs', err);
                    this.toastr.error('Could not load blogs');
                }
            });
    }

    prev(): void {
        this.swiper?.slidePrev();
    }

    next(): void {
        this.swiper?.slideNext();
    }

    private loadAvatar(blog: Blog): void {
        if (this.avatars[blog.authorId]) return;
        this.profileImages.photo(blog.authorId, blog.authorPhotoUri).subscribe(url => {
            if (url) this.avatars[blog.authorId] = url;
        });
    }

    initials(name: string): string {
        return (name || '?').split(' ').filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('');
    }
}
